import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import qs from 'qs';
import { ROLES } from '@/helpers/constants';
import { APP_LINKS } from '@/helpers/links';
import { useGetMe } from '@/hooks/shared/useGetMe';
import { useGetUserLocation } from '@/query/queries/user/useGetUserLocation';
import { useValidatePhoneNumber } from '@/query/queries/user/useValidatePhoneNumber';
import { useRegisterUser } from '@/query/queries/user/useRegisterUser';
import { useLoginUser } from '@/query/queries/user/useLoginUser';
import { uploadFile, uploadImage } from '@/store/services/commonServices';
import { useUpdateContactInfo } from '@/query/queries/user/useUpdateContactInfo';
import { useSendPhoneVerificationCode } from '@/query/queries/user/useSendPhoneVerificationCode';
import RegisterForm from '@/components/Forms/RegisterForm';
import VerifyForm from '@/components/VerifyForm';
import { cropAvatar } from '@/components/AvatarEdit';
import ContactInfoForm from '@/components/Forms/ContactInfoForm';
import PhoneVerificationForm from '@/components/Forms/PhoneVerificationForm';
import { useReCaptcha } from '@/providers/recaptcha';
import './index.scss';

const STEPS = {
  main: 'main',
  contact: 'contact',
  phone_verification: 'phone_verification',
  veriff: 'veriff',
};

const SignUp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { mutateAsync: loginUser } = useLoginUser();
  const { mutateAsync: registerUser } = useRegisterUser();
  const { t } = useTranslation('auth');
  const { step: queryStep } = qs.parse(location.search.replace('?', ''));
  const { user, profile } = useGetMe();
  const { data: userLocation } = useGetUserLocation();
  const { mutateAsync: validatePhoneNumber } = useValidatePhoneNumber();
  const { mutateAsync: contactInfoUpdate } = useUpdateContactInfo();
  const { mutateAsync: sendVerificationCode } = useSendPhoneVerificationCode();
  const { token: captchaToken, getCaptchaToken } = useReCaptcha();

  const [phone, setPhone] = useState('');
  const [step, setStep] = useState(
    user && Object.keys(STEPS).includes(queryStep)
      ? queryStep
      : profile
        ? STEPS.contact
        : STEPS.main,
  );

  useEffect(() => {
    if (profile && step === STEPS.main) {
      setStep(STEPS.contact);
    }
  }, [profile, step]);

  const getStepNumber = step => {
    const index = Object.keys(STEPS).findIndex(key => key === step);
    return index >= 0 ? index + 1 : 1;
  };

  const onRegister = async (values, { setSubmitting }) => {
    try {
      const payload = {
        email: values.email,
        password: values.password,
        invite_code: null,
      };
      const res = await registerUser(payload);

      if (!res) {
        setSubmitting(false);
        return;
      }

      if (res.success) {
        setStep(STEPS.contact);
        return;
      }

      if (res?.data.message === 'User with this email already registered') {
        sessionStorage.setItem('existingEmail', values.email);
        const res = await loginUser({ email: values.email, password: values.password });
        res && res.success ? navigate(APP_LINKS.dashboard) : navigate(APP_LINKS.signIn);
      }

    } finally {
      setSubmitting(false);
    }
  };

  const onContactInfoSubmit = async (data, formikProps, editorRef) => {
    const { setSubmitting, errors, setErrors } = formikProps;

    try {
      const payload = {
        phone: data.phone,
        first_name: data.first_name,
        last_name: data.last_name,
        citizenship: data.citizenship,
        citizenship_code: data.citizenship_code,
        company_name: data.company_name,
        company_description: data.description,
        description: data.about,
        cv: null,
        photo: null,
      };

      if (data.photo && editorRef) {
        const croppedAvatar = cropAvatar(editorRef);
        const res = await uploadImage(croppedAvatar);
        res && res.success && (payload.photo = res.data.id);
      }

      if (data.resume) {
        const res = await uploadFile(data.resume);
        res && res.id && (payload.cv = res.id);
      }

      const res = await contactInfoUpdate(payload);
      if (res && res.success) {
        setPhone(data.phone);
        const token = captchaToken || (await getCaptchaToken());
        if (!token) return;
        await sendVerificationCode({ phone_number: data.phone, recaptcha_token: token });
        setStep(STEPS.phone_verification);
      }

      if (res && !res.success && res.errors) {
        setErrors({ ...errors, ...res.errors });
        window.scrollTo(0, 0);
      }
    } catch {
      // Do nothing
    } finally {
      setSubmitting(false);
    }
  };

  const onPhoneVerification = async (values, { setErrors }) => {
    if (values.code.length < 6) {
      return setErrors({ code: t('sign_up.verification_code_required') });
    }

    const res = await validatePhoneNumber({
      phone_number: phone,
      code: values.code.join(''),
    });

    if (res) {
      res.success
        ? setStep(STEPS.veriff)
        : res.data?.message && setErrors({ code: res.data.message });
    }
  };

  const profileUser = profile?.user;

  const userData = useMemo(
    () => ({
      ...user,
      ...profileUser,
      cv: profile ? profile.cv : null,
      description: profile ? profile.description : '',
      company: profile ? profile.company : null,
    }),
    [profile, profileUser, user],
  );

  return (
    <div className="sign-up">
      <div className="sign-up__inner">
        <div className="sign-up__step-number">
          {t('sign_up.step_of', { step: getStepNumber(step), total: Object.keys(STEPS).length })}
        </div>

        {step === STEPS.main && (
          <RegisterForm
            onSubmit={onRegister}
            nextStep={() => setStep(STEPS.contact)}
            role={ROLES.founder}
          />
        )}

        {step === STEPS.contact && (
          <ContactInfoForm
            onSubmit={onContactInfoSubmit}
            userLocation={userLocation}
            user={userData}
            role={ROLES.founder}
          />
        )}

        {step === STEPS.phone_verification && (
          <PhoneVerificationForm
            phone={phone}
            captchaToken={captchaToken}
            getCaptchaToken={getCaptchaToken}
            onSubmit={onPhoneVerification}
            onLater={() => setStep(STEPS.veriff)}
          />
        )}

        {step === STEPS.veriff && <VerifyForm />}
      </div>
    </div>
  );
};

export default SignUp;
